import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Icon from '../../../../../../UI/Icon'
import { mapCompanyTypeToName } from '../../../../../../shared/helpers'

import styles from './style.module.scss'

/*
 * Welche betreute Junior Company das Dashboard zeigt — und wie man sie
 * wechselt.
 *
 * Bis 23.08.2026 stand der Name links und ein Auswahlfeld mit demselben Namen
 * rechts am Bildschirmrand. Zweimal dasselbe an zwei Enden einer Zeile liest
 * sich nicht als „hier kann ich wechseln", sondern als Wiederholung — und wer
 * das Feld doch fand, sah die aufgeklappte Liste hinter dem Fortschrittsbalken
 * verschwinden.
 *
 * Jetzt ist der Name selbst der Schalter: ein Knopf mit Rahmen, Pfeil und dem
 * Wort „wechseln". Die Liste hängt darunter, nennt zu jeder Company Form und
 * Genehmigungsstand und markiert die gerade gezeigte.
 *
 * Die Liste liegt in einem eigenen Stapelzusammenhang über dem Seiteninhalt
 * (siehe `.root` im Stilblatt) — der Fortschrittsbalken darunter setzt seine
 * Knoten auf `z-index: 1` und lag sonst darüber.
 */

// Ab wann die Suche nötig ist: darunter sieht man alle Einträge auf einmal.
const SUCHE_AB = 8

export default function CompanyPicker({ companies, companyId, onChange }) {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)
	const [search, setSearch] = useState('')
	const rootRef = useRef(null)
	const buttonRef = useRef(null)
	const searchRef = useRef(null)
	const listRef = useRef(null)

	const company = companies.find(item => item.id === companyId)
	const mehrere = companies.length > 1
	const mitSuche = companies.length >= SUCHE_AB

	// Klick daneben und Esc schließen die Liste — wie bei der Filterleiste.
	useEffect(() => {
		if (!open) return undefined

		const onPointerDown = event => {
			if (!rootRef.current?.contains(event.target)) setOpen(false)
		}
		const onKeyDown = event => {
			if (event.key !== 'Escape') return
			event.stopPropagation()
			setOpen(false)
			buttonRef.current?.focus()
		}

		document.addEventListener('mousedown', onPointerDown)
		document.addEventListener('keydown', onKeyDown)
		return () => {
			document.removeEventListener('mousedown', onPointerDown)
			document.removeEventListener('keydown', onKeyDown)
		}
	}, [open])

	useEffect(() => {
		if (!open) {
			setSearch('')
			return
		}
		// Aufgeklappt wird dort weitergearbeitet, wo etwas zu tun ist: im
		// Suchfeld, sonst bei der gerade gezeigten Company.
		if (mitSuche) searchRef.current?.focus()
		else listRef.current?.querySelector(`.${styles.optionCurrent}`)?.focus()
	}, [open, mitSuche])

	const gefiltert = companies.filter(item => item.name?.toLowerCase().includes(search.trim().toLowerCase()))

	// Pfeiltasten wandern durch die Liste, Pos1/Ende springen an die Enden.
	const onListKeyDown = event => {
		const eintraege = Array.from(listRef.current?.querySelectorAll('button') || [])
		if (!eintraege.length) return

		const index = eintraege.indexOf(document.activeElement)
		const springe = ziel => {
			event.preventDefault()
			eintraege[Math.max(0, Math.min(eintraege.length - 1, ziel))]?.focus()
		}

		if (event.key === 'ArrowDown') springe(index + 1)
		else if (event.key === 'ArrowUp') springe(index - 1)
		else if (event.key === 'Home') springe(0)
		else if (event.key === 'End') springe(eintraege.length - 1)
	}

	const waehlen = id => {
		onChange(id)
		setOpen(false)
		buttonRef.current?.focus()
	}

	const stand = item =>
		item.isActive
			? t('Company approved')
			: item.isNeedReview
			? t('Company submitted for approval')
			: t('Company not yet approved')

	return (
		<div className={styles.root} ref={rootRef}>
			<span className={styles.label}>{t('Junior Company')}</span>

			{mehrere ? (
				<button
					type="button"
					ref={buttonRef}
					className={clsx(styles.trigger, open && styles.triggerOpen)}
					onClick={() => setOpen(!open)}
					aria-haspopup="menu"
					aria-expanded={open}
					aria-label={`${company?.name || ''} — ${t('Switch Junior Company')}`}
				>
					<span className={styles.triggerName}>{company?.name}</span>
					<span className={styles.triggerAction}>
						<span className={styles.triggerActionText}>{t('Switch Junior Company')}</span>
						<Icon
							className={clsx(styles.caret, open && styles.caretOpen)}
							name="arrow"
							size={{ w: 10, h: 10 }}
							color="#008b9c"
						/>
					</span>
				</button>
			) : (
				<h2 className={styles.single}>{company?.name}</h2>
			)}

			<span className={styles.meta}>
				{mapCompanyTypeToName(company?.companyType)}
				{company && (
					<span className={clsx(styles.status, company.isActive && styles.statusActive)}>
						{stand(company)}
					</span>
				)}
			</span>

			{open && (
				<div className={styles.menu} role="menu" aria-label={t('Switch Junior Company')}>
					<p className={styles.menuHead}>
						{t('You supervise {{total}} Junior Companies', { total: companies.length })}
					</p>

					{mitSuche && (
						<div className={styles.menuSearch}>
							<input
								ref={searchRef}
								className={styles.menuInput}
								value={search}
								placeholder={t('Search')}
								onChange={event => setSearch(event.target.value)}
								onKeyDown={event => {
									if (event.key === 'ArrowDown') onListKeyDown(event)
								}}
							/>
						</div>
					)}

					<div className={styles.menuList} ref={listRef} onKeyDown={onListKeyDown}>
						{gefiltert.map(item => (
							<button
								type="button"
								key={item.id}
								role="menuitemradio"
								aria-checked={item.id === companyId}
								className={clsx(styles.option, item.id === companyId && styles.optionCurrent)}
								onClick={() => waehlen(item.id)}
							>
								<span className={styles.optionText}>
									<span className={styles.optionName}>{item.name}</span>
									<span className={styles.optionMeta}>
										{[mapCompanyTypeToName(item.companyType), stand(item)]
											.filter(Boolean)
											.join(' · ')}
									</span>
								</span>
								{item.id === companyId && (
									<span className={styles.optionMark} title={t('Currently shown')}>
										{/* Eigenes SVG: die Anwendung setzt global `svg { flex: 1 1 0% }`. */}
										<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
											<path
												d="M3 8.6 6.2 12 13 4.8"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</span>
								)}
							</button>
						))}

						{!gefiltert.length && <p className={styles.menuEmpty}>{t('No entries')}</p>}
					</div>
				</div>
			)}
		</div>
	)
}

CompanyPicker.propTypes = {
	companies: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string,
			companyType: PropTypes.string,
			isActive: PropTypes.bool,
			isNeedReview: PropTypes.bool
		})
	).isRequired,
	companyId: PropTypes.number,
	onChange: PropTypes.func.isRequired
}
